export interface PuzzlePoint {
  x: number
  y: number
}

// Vector scenes rendered crisp at any pixel ratio; one is picked per attempt. index.html preloads the same list.
export const PUZZLE_PICTURES = [
  "/images/captcha/dusk-mountains.svg",
  "/images/captcha/moonlit-sea.svg",
  "/images/captcha/night-city.svg",
  "/images/captcha/pine-forest.svg",
  "/images/captcha/ringed-planet.svg"
] as const

export type PuzzlePicture = (typeof PUZZLE_PICTURES)[number]

// All geometry lives in a fixed 320-unit board space; the UI scales it to the rendered width.
export const BOARD_SIZE = 320
export const PIECE_SIZE = 60
export const PIECE_TAB = PIECE_SIZE / 4
// The piece canvas pads the body by one tab on every side so the top and right tabs fit.
export const PIECE_CANVAS_SIZE = PIECE_SIZE + PIECE_TAB * 2
export const SNAP_RADIUS = 20

export const PIECE_START: PuzzlePoint = { x: 16, y: BOARD_SIZE - PIECE_SIZE - 16 }

// Keeps the gap in the busy middle of every scene and far enough from the start that a tap without a drag never lands.
const TARGET_RANGE = {
  x: { min: 100, max: 200 },
  y: { min: 66, max: 176 }
} as const

const PIECE_BOUNDS = {
  x: { min: 0, max: BOARD_SIZE - PIECE_SIZE - PIECE_TAB },
  y: { min: PIECE_TAB, max: BOARD_SIZE - PIECE_SIZE }
} as const

const pickInRange = (range: { min: number; max: number }, random: () => number) =>
  range.min + Math.floor(random() * (range.max - range.min + 1))

export const pickTarget = (random: () => number = Math.random): PuzzlePoint => ({
  x: pickInRange(TARGET_RANGE.x, random),
  y: pickInRange(TARGET_RANGE.y, random)
})

export const pickPicture = (random: () => number = Math.random): PuzzlePicture =>
  PUZZLE_PICTURES[Math.min(PUZZLE_PICTURES.length - 1, Math.floor(random() * PUZZLE_PICTURES.length))]

const clamp = (value: number, range: { min: number; max: number }) => Math.min(range.max, Math.max(range.min, value))

export const clampPiece = (point: PuzzlePoint): PuzzlePoint => ({
  x: clamp(point.x, PIECE_BOUNDS.x),
  y: clamp(point.y, PIECE_BOUNDS.y)
})

// Any 10-unit grid passes within ~7 units of every gap, well inside the snap radius.
const KEYBOARD_STEP = 10
const KEYBOARD_COARSE_STEP = KEYBOARD_STEP * 4

const KEYBOARD_DIRECTIONS: Readonly<Partial<Record<string, PuzzlePoint>>> = {
  ArrowLeft: { x: -1, y: 0 },
  ArrowRight: { x: 1, y: 0 },
  ArrowUp: { x: 0, y: -1 },
  ArrowDown: { x: 0, y: 1 }
}

export const nudgePiece = (point: PuzzlePoint, key: string, coarse = false): PuzzlePoint | null => {
  const direction = KEYBOARD_DIRECTIONS[key]
  if (!direction) return null
  const step = coarse ? KEYBOARD_COARSE_STEP : KEYBOARD_STEP
  return clampPiece({ x: point.x + direction.x * step, y: point.y + direction.y * step })
}

export const isWithinSnap = (point: PuzzlePoint, target: PuzzlePoint) =>
  Math.hypot(point.x - target.x, point.y - target.y) <= SNAP_RADIUS

// Jigsaw outline with its body's top-left corner at (x, y): tabs out on the top and right edges.
export const piecePath = ({ x, y }: PuzzlePoint): string => {
  const size = PIECE_SIZE
  const tab = PIECE_TAB
  const center = size / 2
  const neckStart = center - tab / 2
  const neckEnd = center + tab / 2

  return [
    `M ${x} ${y}`,
    `H ${x + neckStart}`,
    `C ${x + neckStart} ${y - tab} ${x + neckEnd} ${y - tab} ${x + neckEnd} ${y}`,
    `H ${x + size}`,
    `V ${y + neckStart}`,
    `C ${x + size + tab} ${y + neckStart} ${x + size + tab} ${y + neckEnd} ${x + size} ${y + neckEnd}`,
    `V ${y + size}`,
    `H ${x}`,
    "Z"
  ].join(" ")
}

export const PUZZLE_COLORS = {
  gap: "rgba(17, 24, 39, 0.78)",
  gapOutline: "rgba(255, 255, 255, 0.75)",
  gapOutlineActive: "#eab308",
  pieceOutline: "rgba(255, 255, 255, 0.9)",
  fallbackBase: "#1f2937",
  fallbackStripe: "#374151",
  fallbackAccent: "rgba(234, 179, 8, 0.35)"
} as const
